import { useState, useSyncExternalStore } from 'react'
import { Outlet, createRootRoute, useRouterState } from '@tanstack/react-router'
import { LayoutApp } from '@/app/layouts/LayoutApp'
import { LayoutDespacho } from '@/app/layouts/LayoutDespacho'
import { ProveedorAvisos } from '@/ui/Avisos'
import { LogoPrincipal, URL_SITIO } from '@/ui/marca/Logo'
import { ErrorApi } from '@/api/errores'
import {
  ContribuyentePendienteError,
  iniciarSesion,
  sesionActual,
  suscribirse,
  type AccesoContribuyente,
  type DespachoDisponible,
} from '@/dominio/sesion/sesion'

export const Route = createRootRoute({
  component: Raiz,
  notFoundComponent: NoEncontrada,
})

/** `/consola` y lo que cuelga de ella; `/consolas` o `/consolidado` no. */
function esRutaDeLaConsola(ruta: string): boolean {
  return ruta === '/consola' || ruta.startsWith('/consola/')
}

/**
 * La raíz decide lo primero de todo: si hay sesión se entra, y si no se pide.
 *
 * El portal entero cuelga de aquí, así que la comprobación va en un sitio y no
 * ruta por ruta. Una pantalla que se olvide de comprobarlo no llamaría a la
 * API sin testigo —el servidor devolvería 401 igual— pero le enseñaría al
 * usuario un armazón vacío con mensajes de error, que es la peor forma de
 * decir «no has entrado».
 */
function Raiz() {
  const sesion = useSyncExternalStore(suscribirse, sesionActual, () => null)
  const enConsola = useRouterState({ select: (estado) => esRutaDeLaConsola(estado.location.pathname) || /^\/empleado(\/|$)/.test(estado.location.pathname) })

  // La consola del dueño tiene su propia entrada, su propio testigo y su propio
  // armazón (`rutas/consola.tsx`, docs/09 §6). No pasa por la sesión del portal:
  // pedirle al operador la credencial del portal para llegar a la suya sería
  // mezclar las dos sesiones que precisamente se separan.
  if (enConsola) {
    return (
      <ProveedorAvisos>
        <Outlet />
      </ProveedorAvisos>
    )
  }

  if (sesion === null || (sesion.activo === null && sesion.despacho === null)) {
    return (
      <ProveedorAvisos>
        <PantallaAcceso />
      </ProveedorAvisos>
    )
  }

  // La sesión de un despacho contable no lleva contribuyente: su armazón es el
  // de la cartera, no el de un cliente (docs/adr/0021).
  if (sesion.despacho !== null) {
    return (
      <ProveedorAvisos>
        <LayoutDespacho>
          <Outlet />
        </LayoutDespacho>
      </ProveedorAvisos>
    )
  }

  return (
    <ProveedorAvisos>
      <LayoutApp>
        <Outlet />
      </LayoutApp>
    </ProveedorAvisos>
  )
}

function PantallaAcceso() {
  const [email, setEmail] = useState('')
  const [credencial, setCredencial] = useState('')
  const [tenant, setTenant] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [entrando, setEntrando] = useState(false)
  const [aElegir, setAElegir] = useState<AccesoContribuyente[] | null>(null)
  const [despachosAElegir, setDespachosAElegir] = useState<DespachoDisponible[]>([])

  async function entrar(conTenant: string, conDespacho?: string) {
    setError(null)
    setEntrando(true)

    try {
      await iniciarSesion(email.trim(), credencial, conTenant, conDespacho)
    } catch (fallo) {
      if (fallo instanceof ContribuyentePendienteError) {
        // La credencial era buena: solo falta decir con cuál de los suyos
        // entra. Se conserva escrita para que elegir sea un clic y no volver
        // a teclearla.
        setAElegir(fallo.disponibles)
        setDespachosAElegir(fallo.despachos)
        setEntrando(false)
        return
      }

      // El mensaje no distingue «ese correo no existe» de «esa credencial no
      // es». Distinguirlos convierte la pantalla en un comprobador de qué
      // correos tienen cuenta, que es el primer paso de cualquier ataque
      // dirigido a un contribuyente concreto.
      setError(
        fallo instanceof ErrorApi && fallo.clase === 'red'
          ? 'No hay conexión con el servidor.'
          : 'El correo, la credencial o el contribuyente no coinciden.',
      )
      setCredencial('')
      setAElegir(null)
    } finally {
      setEntrando(false)
    }
  }

  async function enviar(evento: React.FormEvent) {
    evento.preventDefault()
    await entrar(tenant.trim())
  }

  if (aElegir !== null) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gris-100 px-4">
        <div className="flex w-full max-w-[380px] flex-col gap-4 rounded-md border border-gris-300 bg-white p-6 shadow-flotante">
          <div className="flex flex-col gap-1">
            <h1 className="text-pantalla text-gris-900">¿Con cuál entras?</h1>
            <p className="text-menor text-gris-600">
              Esta cuenta lleva {aElegir.length} contribuyente(s)
              {despachosAElegir.length > 0 ? ` y ${despachosAElegir.length} despacho(s) contable(s)` : ''}.
            </p>
          </div>

          {/*
            El despacho primero: quien lleva una cartera entra a ella y desde ahí a
            cada cliente sin volver a teclear la credencial (docs/adr/0021).
          */}
          {despachosAElegir.length > 0 ? (
            <ul className="flex flex-col gap-2">
              {despachosAElegir.map((d) => (
                <li key={d.despacho}>
                  <button
                    type="button"
                    disabled={entrando}
                    onClick={() => void entrar('', d.despacho)}
                    className="flex w-full flex-col items-start gap-0.5 rounded border border-marca px-3 py-2 text-left hover:bg-marca-tenue disabled:opacity-60"
                  >
                    <span className="text-cuerpo text-gris-900">{d.nombre}</span>
                    <span className="text-menor text-gris-600">
                      Despacho contable · cartera de clientes · {d.rol}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          ) : null}

          <ul className="flex flex-col gap-2">
            {aElegir.map((acceso) => (
              <li key={acceso.tenant}>
                <button
                  type="button"
                  disabled={entrando}
                  onClick={() => void entrar(acceso.tenant)}
                  className="flex w-full flex-col items-start gap-0.5 rounded border border-gris-300 px-3 py-2 text-left hover:border-marca hover:bg-marca-tenue disabled:opacity-60"
                >
                  <span className="text-cuerpo text-gris-900">{acceso.razonSocial}</span>
                  <span className="numero text-menor text-gris-600">
                    {acceso.rnc} · {acceso.rol}
                    {acceso.despachoNombre ? ` · vía ${acceso.despachoNombre}` : ''}
                  </span>
                </button>
              </li>
            ))}
          </ul>

          <button
            type="button"
            onClick={() => {
              setAElegir(null)
              setDespachosAElegir([])
              setCredencial('')
            }}
            className="text-menor text-gris-600 underline"
          >
            Entrar con otra cuenta
          </button>
        </div>
      </div>
    )
  }

  // El logo va arriba y la salida al sitio abajo: al portal se llega desde el sitio de la
  // empresa, y quien entró por error tiene por dónde volver.
  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-gris-100 px-4">
      <form
        onSubmit={(e) => void enviar(e)}
        className="flex w-full max-w-[380px] flex-col gap-4 rounded-md border border-gris-300 bg-white p-6 shadow-flotante"
      >
        <div className="flex flex-col gap-5">
          <LogoPrincipal className="h-9 w-auto self-start text-gris-900" />
          <div className="flex flex-col gap-1">
            <h1 className="text-pantalla text-gris-900">Entrar</h1>
            <p className="text-menor text-gris-600">Portal de facturación electrónica</p>
          </div>
        </div>

        <label className="flex flex-col gap-1">
          <span className="text-menor text-gris-700">Correo</span>
          <input
            type="email"
            required
            autoFocus
            autoComplete="username"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="rounded border border-gris-300 px-2 py-1.5 text-cuerpo"
          />
        </label>

        <label className="flex flex-col gap-1">
          <span className="text-menor text-gris-700">Credencial</span>
          <input
            type="password"
            required
            autoComplete="current-password"
            value={credencial}
            onChange={(e) => setCredencial(e.target.value)}
            className="rounded border border-gris-300 px-2 py-1.5 text-cuerpo"
          />
        </label>

        <label className="flex flex-col gap-1">
          <span className="text-menor text-gris-700">
            Contribuyente <span className="text-gris-600">(opcional)</span>
          </span>
          {/*
            Sin `required`, y esto no es relajar una validación: es quitar una que
            contradecía al servidor y a la etiqueta de debajo. El extremo acepta
            `tenant` nulo y resuelve el único al que el usuario tiene acceso; con
            `required` puesto, quien trabaja con un solo contribuyente **no podía
            entrar** sin teclear un identificador que no tiene por qué conocer.

            Va aquí y no se deduce del correo porque un usuario puede tener acceso a
            varios —el contador externo es el caso normal— y el servidor firma el
            testigo con este dentro.
          */}
          <input
            value={tenant}
            onChange={(e) => setTenant(e.target.value)}
            placeholder="Identificador del contribuyente"
            className="rounded border border-gris-300 px-2 py-1.5 font-mono text-menor"
          />
          <span className="text-menor text-gris-600">
            Déjalo vacío si solo trabajas con uno. Con varios, indica con cuál entras.
          </span>
        </label>

        {error !== null && (
          <p role="alert" className="rounded border border-rojo-300 bg-rojo-50 px-2 py-1.5 text-menor text-rojo-700">
            {error}
          </p>
        )}

        <button
          type="submit"
          disabled={entrando}
          className="rounded bg-marca px-3 py-2 text-cuerpo text-white disabled:opacity-60"
        >
          {entrando ? 'Entrando…' : 'Entrar'}
        </button>
      </form>

      <a href={URL_SITIO} className="text-menor text-gris-600 hover:text-gris-900">
        ← Volver al sitio de Agentical Group
      </a>
    </div>
  )
}

function NoEncontrada() {
  return (
    <div className="flex max-w-[520px] flex-col gap-2">
      <h1 className="text-pantalla text-gris-900">Esa dirección no existe.</h1>
      <p className="text-cuerpo text-gris-600">
        Revisa el enlace. Si llegaste desde dentro de la aplicación, avísanos con la dirección exacta.
      </p>
    </div>
  )
}
